import api from "./api";
import { notImplemented } from "../utils/errors";
import { computePerformance, DEFAULT_BANDS, DEFAULT_WEIGHTS, getRating, NOT_RATED, round2 } from "../utils/scoreCalculator";
import { setDisplayScale, pipBandsOfScheme, setPipBands } from "../utils/ratingScale"; // [NEW] PM-13, PM-9

export const ok = (data) => ({ data });
export const REASON_MAX = 1000;
export const reasonOf = (text, fallback) => {
  const value = String(text ?? "").trim();
  if (value.length > REASON_MAX) {
    throw new Error(`Please shorten your comment to ${REASON_MAX} characters (it is ${value.length} now).`);
  }
  return value || fallback;
};

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const cap = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : s);

export async function pMap(items, fn, limit = 6) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await fn(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

export async function pMapSettled(items, fn, limit = 6, fallback = null) {
  return pMap(items, async (item, i) => {
    try {
      return await fn(item, i);
    } catch {
      return fallback;
    }
  }, limit);
}

export function parseUtc(value) {
  if (!value) return null;
  const text = String(value).replace(" ", "T");
  return new Date(/[zZ]$|[+-]\d\d:\d\d$/.test(text) ? text : `${text}Z`);
}
export const isoUtc = (value) => (value ? parseUtc(value).toISOString() : null);
const pad = (n) => String(n).padStart(2, "0");
export const localDate = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "");
export const utcToLocalDate = (value) => (value ? localDate(parseUtc(value)) : "");
export const dayStartIso = (day) => new Date(`${day}T00:00:00`).toISOString();
export const dayEndIso = (day) => new Date(`${day}T23:59:59`).toISOString();
export const utcToday = () => new Date().toISOString().slice(0, 10);
export const addDays = (day, n) => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  return localDate(d);
};

let meCache = null;
let mePromise = null;

export async function getMe(force = false) {
  if (force || (!meCache && !mePromise)) {
    mePromise = api.get("/auth/me").then((res) => {
      meCache = res.data;
      rememberName(meCache.employee_id, meCache.employee_name);
      return meCache;
    }).finally(() => { mePromise = null; });
  }
  return meCache || mePromise;
}

export function resetCaches() {
  meCache = null;
  lookupsPromise = null;
  cyclesPromise = null;
  configPromise = null;
  names.clear();
  itemCache.clear();
}

export const ORG_SCOPES = ["SYSTEM_ALL", "PERFORMANCE_ORG"];
export function can(me, permission, scopes) {
  return (me?.grants || []).some((g) => g.permission_code === permission && (!scopes || scopes.includes(g.data_scope)));
}
export const roleCodesOf = (me) => [...new Set((me?.grants || []).map((g) => g.role_code))];

export const ROLE_CODE_TO_KEY = {
  ADMIN: "admin",
  IT_ADMIN: "it_admin", // [NEW] PM-5: IT staff, Admin view with users and audit trail only
  PERFORMANCE_HR: "hr",
  MANAGEMENT: "management",
  MANAGER: "manager",
  EMPLOYEE: "employee",
};
export const ROLE_KEY_TO_CODE = Object.fromEntries(Object.entries(ROLE_CODE_TO_KEY).map(([k, v]) => [v, k]));
const ROLE_PRIORITY = ["admin", "it_admin", "hr", "management", "manager", "employee"];

export function roleKeysOf(me) {
  const keys = roleCodesOf(me).map((c) => ROLE_CODE_TO_KEY[c]).filter(Boolean);
  if (!keys.includes("employee")) keys.push("employee");
  return ROLE_PRIORITY.filter((k) => keys.includes(k));
}
export const primaryRoleOf = (me) => roleKeysOf(me)[0] || "employee";

const names = new Map();
export function rememberName(id, name) {
  if (id && name) names.set(String(id), name);
}
export const nameOf = (id, fallback) => (id ? names.get(String(id)) || fallback || String(id) : fallback || "—");
// [NEW] The loaded name for an ID, or "" when it is not known (nameOf would return the ID itself).
export const knownNameOf = (id) => (id ? names.get(String(id)) || "" : "");

export const GOAL_CATEGORIES = [
  "Business", "Customer", "Process improvement", "Learning & growth", "Team & leadership", "Compliance",
].map((name) => ({ id: name, name }));

export const LOCATIONS = ["Pune", "Mumbai", "Bengaluru", "Hyderabad", "Chennai", "Delhi NCR", "Remote"];

let lookupsPromise = null;

async function warmNames() {
  try {
    const me = await getMe();
    if (!can(me, "employee.manage", ORG_SCOPES)) return;
    for (let offset = 0; offset < 20000; offset += 200) {
      const { data } = await api.get("/employees", { params: { limit: 200, offset } });
      data.forEach((e) => rememberName(e.employee_id, e.employee_name));
      if (data.length < 200) break;
    }
  } catch {
  }
}

export function getLookupsCached(force = false) {
  if (force || !lookupsPromise) {
    lookupsPromise = Promise.all([api.get("/lookups"), warmNames()]).then(([res]) => ({
      departments: (res.data.department || []).map((d) => ({ id: String(d.department_id), code: d.department_code, name: d.department_name })),
      designations: (res.data.designation || []).map((d) => ({ id: String(d.designation_id), code: d.designation_code, name: d.designation_name })),
    })).catch((err) => { lookupsPromise = null; throw err; });
  }
  return lookupsPromise;
}

const CYCLE_STATUS = {
  DRAFT: "Draft", OPEN: "Active", REVIEW_IN_PROGRESS: "Active", FINALIZATION: "Active", CLOSED: "Closed", CANCELLED: "Cancelled",
};
export const STAGE_WINDOWS = [
  { key: "goalSetting", prefix: "goal", backend: "GOAL_SETTING" },
  { key: "selfAssessment", prefix: "self_review", backend: "SELF_ASSESSMENT" },
  { key: "managerReview", prefix: "manager_review", backend: "MANAGER_ASSESSMENT" },
  { key: "hrFinalization", prefix: "hr_finalization", backend: "HR_FINALIZATION" },
];

function windowStatus(startAt, endAt, cycleStatus) {
  const now = Date.now();
  const start = parseUtc(startAt)?.getTime();
  const end = parseUtc(endAt)?.getTime();
  if (cycleStatus === "CLOSED" || cycleStatus === "CANCELLED") return "closed";
  if (!start || !end) return "upcoming";
  if (now < start) return "upcoming";
  if (now >= end) return "closed";
  return cycleStatus === "DRAFT" ? "upcoming" : "open";
}

export function toCycle(c) {
  return {
    id: String(c.cycle_id),
    cycleId: c.cycle_id,
    name: c.cycle_name,
    startDate: c.performance_start,
    endDate: c.performance_end,
    status: CYCLE_STATUS[c.cycle_status] || c.cycle_status,
    backendStatus: c.cycle_status,
    ratingScale: c.rating_scale || null, // [NEW] PM-13: "0_5" | "1_10" from the new backend
    stages: STAGE_WINDOWS.map((s) => ({
      key: s.key,
      backendStage: s.backend,
      startDate: utcToLocalDate(c[`${s.prefix}_start_at`]),
      endDate: utcToLocalDate(c[`${s.prefix}_end_at`]),
      startAt: isoUtc(c[`${s.prefix}_start_at`]),
      endAt: isoUtc(c[`${s.prefix}_end_at`]),
      status: windowStatus(c[`${s.prefix}_start_at`], c[`${s.prefix}_end_at`], c.cycle_status),
    })),
    scoringPolicyId: c.scoring_policy_id,
    ratingSchemeId: c.rating_scheme_id,
    workflow: c.workflow_config || {},
    configVersion: c.config_version,
    launchedAt: isoUtc(c.launched_at),
    closedAt: isoUtc(c.closed_at),
    raw: c,
  };
}

let cyclesPromise = null;
export function listCyclesCached(force = false) {
  if (force || !cyclesPromise) {
    cyclesPromise = api.get("/performance-cycles").then((res) => res.data.map(toCycle))
      .catch((err) => { cyclesPromise = null; throw err; });
  }
  return cyclesPromise;
}
export async function cycleById(cycleId) {
  const cycles = await listCyclesCached();
  return cycles.find((c) => c.id === String(cycleId)) || null;
}
export const stageOf = (cycle, key) => cycle?.stages?.find((s) => s.key === key) || null;

const EXT_TAG = /\[ext:(GOAL_SETTING|SELF_ASSESSMENT|MANAGER_ASSESSMENT|HR_FINALIZATION)\|([^|\]]+)\|([^|\]]+)\]/;
export const extensionTag = (stage, opensIso, closesIso) => `[ext:${stage}|${opensIso}|${closesIso}]`;
export const stripExtensionTag = (text) => String(text || "").replace(EXT_TAG, "").trim();

export function extensionsFromHistory(history = []) {
  const out = { untagged: false };
  (history || []).filter((h) => h.event_type === "DEADLINE_OVERRIDDEN").forEach((h) => {
    const m = EXT_TAG.exec(h.reason || "");
    if (!m) { out.untagged = true; return; }
    out[m[1]] = { opensAt: m[2], closesAt: m[3], at: h.created_at };
  });
  return out;
}

export function isStageOpenFor(cycle, key, extensions) {
  if (!cycle || cycle.status !== "Active") return false;
  const stage = stageOf(cycle, key);
  const ext = extensions?.[stage?.backendStage];
  if (ext) {
    const now = Date.now();
    return now >= new Date(ext.opensAt).getTime() && now < new Date(ext.closesAt).getTime();
  }
  if (stage?.status === "open") return true;
  return Boolean(extensions?.untagged && stage?.status === "closed");
}
export const extensionFor = (cycle, key, extensions) => extensions?.[stageOf(cycle, key)?.backendStage] || null;

export const isStageOpen = (cycle, key, extensions) => (extensions
  ? isStageOpenFor(cycle, key, extensions)
  : Boolean(cycle && cycle.status === "Active" && stageOf(cycle, key)?.status === "open"));

export async function listRecords(cycleId) {
  const rows = [];
  for (let offset = 0; offset < 5000; offset += 200) {
    const { data } = await api.get("/records", { params: { cycle_id: cycleId, limit: 200, offset } });
    rows.push(...data);
    if (data.length < 200) break;
  }
  return rows;
}

export async function findRecord(cycleId, employeeId) {
  const rows = await listRecords(cycleId);
  const row = rows.find((r) => r.employee_id === employeeId);
  if (!row) {
    const me = await getMe();
    throw new Error(
      employeeId === me.employee_id
        ? "You are not part of this performance cycle yet. HR adds employees to a cycle from Performance cycles."
        : `${nameOf(employeeId)} is not part of this performance cycle, or you don't have access to their record.`
    );
  }
  return row;
}

export async function myRecordSummary(cycleId) {
  const me = await getMe();
  return findRecord(cycleId, me.employee_id);
}

export async function recordDetail(recordId) {
  const { data } = await api.get(`/records/${recordId}`);
  const snap = data.context_snapshot || {};
  rememberName(data.employee_id, snap.employee_name);
  (data.items || []).forEach((item) => itemCache.set(String(item.performance_item_id), { item, record: data }));
  return data;
}

export async function recordSections(recordId, sections) {
  const { data } = await api.get(`/records/${recordId}`, { params: { include: sections } });
  return data.sections || {};
}

export async function recordSection(recordId, section, fallback = null) {
  try {
    const sections = await recordSections(recordId, [section]);
    return sections[section] ?? fallback;
  } catch {
    return fallback;
  }
}

const itemCache = new Map();
export const cachedItem = (id) => itemCache.get(String(id)) || null;

export function goalStatusOf(item) {
  if (item.item_status === "IN_PROGRESS" && Number(item.progress_pct) >= 100) return "Completed";
  return ITEM_STATUS[item.item_status] || item.item_status;
}

export const ITEM_STATUS = {
  DRAFT: "Draft", SUBMITTED: "Submitted", PENDING_APPROVAL: "Manager Review", CHANGES_REQUESTED: "Changes Requested",
  APPROVED: "Approved", IN_PROGRESS: "In Progress", COMPLETED: "Completed", CLOSED: "Closed", CANCELLED: "Cancelled",
};
export const SOURCE_LABEL = {
  EMPLOYEE_PROPOSED: "Employee proposed", MANAGER: "Manager assigned", BULK_ASSIGNMENT: "Organisation cascaded", OTHER: "Role template",
};
export const DIRECTION_TO_UI = { HIGHER_IS_BETTER: "higher", LOWER_IS_BETTER: "lower", TARGET_BASED: "target" };
export const DIRECTION_TO_API = { higher: "HIGHER_IS_BETTER", lower: "LOWER_IS_BETTER", target: "TARGET_BASED" };
export const FREQUENCY_TO_UI = { ONCE: "Once", MONTHLY: "Monthly", QUARTERLY: "Quarterly", ANNUAL: "Annual", CUSTOM: "Half-yearly" };
export const FREQUENCY_TO_API = { Once: "ONCE", Monthly: "MONTHLY", Quarterly: "QUARTERLY", Annual: "ANNUAL", "Half-yearly": "CUSTOM" };
export const PRIORITY_TO_API = { High: "HIGH", Medium: "MEDIUM", Low: "LOW" };

function assessmentItems(assessment) {
  return Object.fromEntries(((assessment || {}).items || []).map((x) => [String(x.item_id), x]));
}

function kraKpiStatus(itemId, self, manager, record) {
  if (record.manager_submitted_at && manager[itemId]) return "Reviewed";
  if (self[itemId]) return "Self Assessed";
  return "Assigned";
}

const EXTRA_MARK = "\n\n[goal-details]\n";
export function encodeGoalExtras(description, { baseline, source, notes } = {}) {
  const lines = [];
  if (baseline !== undefined && baseline !== null && String(baseline).trim() !== "") lines.push(`Baseline: ${String(baseline).trim()}`);
  if (source && String(source).trim()) lines.push(`Source: ${String(source).trim()}`);
  if (notes && String(notes).trim()) lines.push(`Notes: ${String(notes).trim().replace(/\n+/g, " ")}`);
  const base = String(description || "").trim();
  return lines.length ? `${base}${EXTRA_MARK}${lines.join("\n")}`.slice(0, 10000) : base;
}
export function decodeGoalExtras(text) {
  const value = String(text || "");
  const m0 = /\s*\[goal-details\]\n/.exec(value);
  if (!m0) return { description: value, baseline: "", source: "", notes: "" };
  const out = { description: value.slice(0, m0.index), baseline: "", source: "", notes: "" };
  value.slice(m0.index + m0[0].length).split("\n").forEach((line) => {
    const m = /^(Baseline|Source|Notes): (.*)$/.exec(line);
    if (m) out[m[1].toLowerCase()] = m[2];
  });
  return out;
}

export function toItemVm(item, record) {
  const self = assessmentItems(record.self_assessment);
  const manager = assessmentItems(record.manager_assessment);
  const id = String(item.performance_item_id);
  const snap = item.definition_snapshot || {};
  const base = {
    id,
    itemId: item.performance_item_id,
    recordId: record.performance_record_id,
    employeeId: record.employee_id,
    cycleId: String(record.cycle_id),
    version: item.version_no,
    ownerId: item.change_owner_employee_id,
    weightage: num(item.weight),
    target: num(item.target),
    unit: item.measurement_unit,
    dueDate: item.due_date,
    description: item.item_description || snap.description || "",
    createdAt: isoUtc(item.created_at),
    updatedAt: isoUtc(item.updated_at),
    employeeComments: self[id]?.comment || item.employee_comment || "",
    managerComments: manager[id]?.comment || "",
    evidence: null,
    backendStatus: item.item_status,
    assessmentInputRequired: item.assessment_input_required === undefined || item.assessment_input_required === null
      ? true
      : Boolean(item.assessment_input_required),
    parentKraItemId: item.parent_kra_item_id ? String(item.parent_kra_item_id) : null,
    raw: item,
  };
  if (item.item_type === "GOAL") {
    const extras = decodeGoalExtras(item.item_description);
    return {
      ...base,
      description: extras.description,
      notes: extras.notes,
      title: item.item_title,
      categoryId: item.category || "",
      categoryName: item.category || "General",
      priority: cap(item.priority || "MEDIUM"),
      baseline: extras.baseline || (item.baseline ?? ""),
      achievement: num(self[id]?.achievement ?? item.progress_pct),
      managerAchievement: num(manager[id]?.achievement),
      status: goalStatusOf(item),
      source: (item.source_type === "EMPLOYEE_PROPOSED" && extras.source) || SOURCE_LABEL[item.source_type] || extras.source || "",
    };
  }
  const actual = num(manager[id]?.achievement ?? self[id]?.achievement ?? item.actual);
  const common = {
    ...base,
    name: item.item_title,
    actual,
    selfActual: num(self[id]?.achievement),
    status: kraKpiStatus(id, self, manager, record),
  };
  if (item.item_type === "KRA") {
    return { ...common, kraId: String(item.kra_id || ""), measurementMethod: snap.measurement_method || "" };
  }
  return {
    ...common,
    kpiId: String(item.kpi_id || ""),
    frequency: FREQUENCY_TO_UI[item.measurement_frequency] || "Once",
    direction: DIRECTION_TO_UI[item.measurement_direction] || "higher",
  };
}

export function splitItems(record) {
  const items = (record.items || []).map((it) => ({ type: it.item_type, vm: toItemVm(it, record) }));
  const goals = items.filter((x) => x.type === "GOAL").map((x) => x.vm);
  const kras = items.filter((x) => x.type === "KRA").map((x) => x.vm);
  const byItem = Object.fromEntries(kras.map((kra) => [kra.id, kra]));
  const kpis = items.filter((x) => x.type === "KPI").map((x) => ({
    ...x.vm,
    parentKraId: byItem[x.vm.parentKraItemId]?.kraId || null,
    parentKraName: byItem[x.vm.parentKraItemId]?.name || "",
  }));
  return {
    goals,
    kras: kras.map((kra) => ({ ...kra, kpis: kpis.filter((kpi) => kpi.parentKraItemId === kra.id) })),
    kpis,
  };
}

export function componentWeightIssues(record) {
  const issues = [];
  const rows = (record?.items || []).filter((i) => i.item_status !== "CANCELLED");
  const kras = rows.filter((i) => i.item_type === "KRA");
  const kpis = rows.filter((i) => i.item_type === "KPI");
  const kraTotal = Math.round(kras.reduce((s, i) => s + Number(i.weight || 0), 0) * 100) / 100;
  if (kras.length && kraTotal !== 100) issues.push(`KRA weights add up to ${kraTotal}%, not 100%.`);
  kras.forEach((kra) => {
    const children = kpis.filter((kpi) => String(kpi.parent_kra_item_id || "") === String(kra.performance_item_id));
    const total = Math.round(children.reduce((s, i) => s + Number(i.weight || 0), 0) * 100) / 100;
    // [UPDATED] KPI weights are shared equally by the frontend; a total other than 100% means an older plan.
    if (!children.length) issues.push(`${kra.item_title} has no KPI yet. Add at least one KPI under it.`);
    else if (total !== 100) issues.push(`KPI weights under ${kra.item_title} add up to ${total}%. Open Assign KRA & KPI and click Share KPI weights equally.`);
  });
  if (kpis.some((kpi) => !kpi.parent_kra_item_id)) issues.push("Every KPI must belong to a KRA.");
  return issues;
}

export const totalWeight = (rows) => Math.round(rows.reduce((s, r) => s + (Number(r.weightage) || 0), 0) * 100) / 100;

export function itemBody(item, changes = {}, reason = "Updated") {
  return {
    item_title: changes.item_title ?? item.item_title,
    item_description: changes.item_description ?? (item.item_description || ""),
    weight: Number(changes.weight ?? item.weight),
    target: Number(changes.target ?? item.target),
    measurement_unit: changes.measurement_unit ?? item.measurement_unit,
    measurement_direction: changes.measurement_direction ?? (item.measurement_direction || "HIGHER_IS_BETTER"),
    due_date: changes.due_date ?? item.due_date,
    category: changes.category !== undefined ? changes.category : item.category,
    priority: changes.priority ?? (item.priority || "MEDIUM"),
    expected_version: item.version_no,
    reason,
  };
}

export const RESULT_STATES = ["FINALIZED", "RELEASED", "ACKNOWLEDGED"];
const SELF_DONE_STAGES = ["MANAGER_ASSESSMENT", "HR_FINALIZATION", "RELEASED"];
const MANAGER_DONE_STAGES = ["HR_FINALIZATION", "RELEASED"];

export function sheetStatusOf(record) {
  const plan = record.goal_plan;
  const goals = (record.items || []).filter((i) => i.item_type === "GOAL");
  if (plan) {
    if (plan.plan_status === "DRAFT") return goals.length ? "Draft" : "Not Started";
    return { SUBMITTED: "Pending Approval", CHANGES_REQUESTED: "Changes Requested", APPROVED: "Approved", CLOSED: "Approved" }[plan.plan_status] || "Draft";
  }
  if (record.current_stage === "GOAL_SETTING") return record.record_status === "NOT_STARTED" ? "Not Started" : "Draft";
  if (record.current_stage === "GOAL_APPROVAL") return "Pending Approval";
  return "Approved";
}

const hasContent = (a) => a && ((a.items || []).length || Object.values(a.sections || {}).some((v) => String(v || "").trim()));

export function selfStatusOf(record) {
  if (record.self_submitted_at || SELF_DONE_STAGES.includes(record.current_stage) || RESULT_STATES.includes(record.record_status)) return "Submitted";
  return hasContent(record.self_assessment) ? "Draft" : "Not Started";
}
export function managerStatusOf(record) {
  if (record.manager_submitted_at || MANAGER_DONE_STAGES.includes(record.current_stage) || RESULT_STATES.includes(record.record_status)) return "Submitted";
  return hasContent(record.manager_assessment) ? "Draft" : "Not Started";
}
export function resultStatusOf(record) {
  if (record.current_result_revision_id || RESULT_STATES.includes(record.record_status)) return "Finalized";
  if (record.manager_submitted_at || record.current_stage === "HR_FINALIZATION" || record.record_status === "HR_REVIEW") return "Pending HR Review";
  return "Awaiting Assessments";
}

export const DEFAULT_SECTIONS = ["summary", "strengths", "improvement_areas", "development_plan"];
export const SELF_SECTION_MAP = { keyAchievements: "summary", strengths: "strengths", challenges: "improvement_areas", developmentAreas: "development_plan" };
export const MANAGER_SECTION_MAP = { comments: "summary", strengths: "strengths", improvementAreas: "improvement_areas", developmentPlan: "development_plan" };
const SECTION_ALIASES = { key_achievements: "summary", challenges: "improvement_areas", development_areas: "development_plan", comments: "summary" };

export function readSections(sections = {}, map) {
  const normalized = {};
  Object.entries(sections || {}).forEach(([k, v]) => { normalized[SECTION_ALIASES[k] || k] = v; });
  const out = Object.fromEntries(Object.entries(map).map(([uiKey, apiKey]) => [uiKey, normalized[apiKey] || ""]));
  const known = new Set(Object.values(map));
  Object.entries(normalized).forEach(([k, v]) => { if (!known.has(k)) out[`extra:${k}`] = v || ""; });
  return out;
}

export function sectionFieldsFor(allowed, map) {
  const list = allowed && allowed.length ? allowed : DEFAULT_SECTIONS;
  const used = new Set();
  return list.map((apiKey) => {
    const canonical = SECTION_ALIASES[apiKey] || apiKey;
    const uiKey = Object.keys(map).find((u) => map[u] === canonical && !used.has(u));
    if (uiKey) { used.add(uiKey); return { apiKey, uiKey }; }
    return { apiKey, uiKey: `extra:${apiKey}` };
  });
}

export function writeSections(form, map, allowed) {
  const allowedSet = new Set(allowed || DEFAULT_SECTIONS);
  const aliasFor = (apiKey) => {
    if (allowedSet.has(apiKey)) return apiKey;
    return Object.keys(SECTION_ALIASES).find((alias) => SECTION_ALIASES[alias] === apiKey && allowedSet.has(alias));
  };
  const out = {};
  Object.entries(map).forEach(([uiKey, apiKey]) => {
    const key = aliasFor(apiKey);
    const value = String(form[uiKey] ?? "").trim();
    if (key && value) out[key] = value;
  });
  Object.entries(form || {}).forEach(([uiKey, v]) => {
    if (!uiKey.startsWith("extra:")) return;
    const key = uiKey.slice(6);
    const value = String(v ?? "").trim();
    if (allowedSet.has(key) && value) out[key] = value;
  });
  return out;
}

const BAND_TONES = ["success", "primary", "info", "warning", "danger"];

export function toBands(apiBands = []) {
  if (!apiBands.length) return DEFAULT_BANDS;
  const sorted = [...apiBands].sort((a, b) => Number(b.min_score) - Number(a.min_score));
  return sorted.map((b, i) => ({
    id: b.rating_band_id,
    code: b.rating_code,
    label: b.rating_label,
    min: Number(b.min_score),
    max: b.max_score === null || b.max_score === undefined ? Math.max(100, Number(b.min_score)) : round2(Number(b.max_score) - 0.01),
    tone: BAND_TONES[Math.round((i * (BAND_TONES.length - 1)) / Math.max(1, sorted.length - 1))] || "neutral",
  }));
}
export const toWeights = (policy) => (policy
  ? { goals: Number(policy.goal_weight), kra: Number(policy.kra_weight), kpi: Number(policy.kpi_weight) }
  : DEFAULT_WEIGHTS);

let configPromise = null;
export function loadScoringConfig(force = false) {
  if (force || !configPromise) {
    configPromise = getMe().then((me) => {
      if (!can(me, "master.configure", ORG_SCOPES)) return [null, null];
      return Promise.all([
        api.get("/configuration/scoring_policy").then((r) => r.data).catch(() => null),
        api.get("/configuration/rating_scheme").then((r) => r.data).catch(() => null),
      ]);
    }).then(([policies, schemes]) => {
      // [NEW] PM-13: show scores on the scale of the newest approved rating scheme (0 to 5 or 1 to 10).
      const current = [...(schemes || [])].filter((x) => x.scheme_status === "APPROVED")
        .sort((a, b) => (b.created_at > a.created_at ? 1 : -1))[0];
      if (current) { setDisplayScale(null); setPipBands(pipBandsOfScheme(current)); }
      return { policies, schemes, restricted: !policies || !schemes };
    });
  }
  return configPromise;
}

const latest = (rows, statusKey) => {
  const approved = (rows || []).filter((r) => r[statusKey] === "APPROVED");
  const pick = (list) => [...list].sort((a, b) => (b.version_no - a.version_no) || (b.created_at > a.created_at ? 1 : -1))[0];
  return pick(approved) || pick(rows || []) || null;
};

export async function ratingConfigFor(cycle) {
  const { policies, schemes, restricted } = await loadScoringConfig();
  const policy = (policies || []).find((p) => p.scoring_policy_id === cycle?.scoringPolicyId) || latest(policies, "policy_status");
  const scheme = (schemes || []).find((s) => s.rating_scheme_id === cycle?.ratingSchemeId) || latest(schemes, "scheme_status");
  if (scheme) { setDisplayScale(null); setPipBands(pipBandsOfScheme(scheme)); } // PM-13/PM-9: this cycle's rules
  return { weights: toWeights(policy), bands: toBands(scheme?.bands), policy, scheme, restricted };
}

export const publicConfig = (config) => ({
  weights: config.weights,
  bands: config.bands,
  restricted: Boolean(config.restricted),
  scoreCap: config.policy?.score_cap !== undefined && config.policy?.score_cap !== null ? Number(config.policy.score_cap) : null,
});

export function toneForLabel(label, bands) {
  return (bands || DEFAULT_BANDS).find((b) => b.label === label)?.tone || "neutral";
}

export function performanceFromResult(result, bands, status = "Finalized") {
  const label = result.rating_label_snapshot || (result.outcome === "NOT_RATED" ? NOT_RATED : null);
  const breakdown = result.calculation_breakdown || {};
  return {
    goalScore: num(result.goal_score),
    kraScore: num(result.kra_score),
    kpiScore: num(result.kpi_score),
    finalScore: num(result.final_score),
    rating: label || NOT_RATED,
    ratingTone: toneForLabel(label, bands),
    scoringMode: breakdown.scoring_mode || "FLAT",
    effectiveWeights: breakdown.effective_weights || null,
    kraBreakdown: breakdown.kra_breakdown || [],
    status,
  };
}

export function performanceFromPreview(preview, bands, status) {
  const c = preview?.components || {};
  const label = preview?.rating?.rating_label || (preview?.outcome === "NOT_RATED" ? NOT_RATED : null);
  return {
    goalScore: round2(num(c.GOAL)),
    kraScore: round2(num(c.KRA)),
    kpiScore: round2(num(c.KPI)),
    finalScore: num(preview?.final_score),
    rating: label || NOT_RATED,
    ratingTone: toneForLabel(label, bands),
    scoringMode: preview?.scoring_mode || "FLAT",
    effectiveWeights: preview?.effective_weights || null,
    kraBreakdown: preview?.kra_breakdown || [],
    status,
  };
}

export function estimatePerformance({ goals, kras, kpis }, config, status) {
  const perf = computePerformance({
    goals: goals.filter((g) => ["Approved", "In Progress", "Completed", "Closed"].includes(g.status)),
    kras,
    kpis,
    config,
  });
  if (config?.restricted) {
    return { ...perf, finalScore: null, rating: "Pending", ratingTone: "neutral", status, estimated: true };
  }
  return { ...perf, status, estimated: true };
}

export function goalCompletionOf(goals) {
  const approved = goals.filter((g) => ["Approved", "In Progress", "Completed", "Closed"].includes(g.status));
  const scored = approved.map((g) => ({ w: Number(g.weightage) || 0, v: g.managerAchievement ?? g.achievement })).filter((x) => x.v !== null && x.v !== undefined);
  if (!scored.length) return null;
  const weight = scored.reduce((s, x) => s + x.w, 0) || 1;
  return Math.round(scored.reduce((s, x) => s + Math.min(100, Number(x.v)) * x.w, 0) / weight);
}

const EMPLOYMENT = { ACTIVE: "Active", INACTIVE: "Inactive", EXITED: "Exited" };
export const EMPLOYMENT_TO_API = { Active: "ACTIVE", Inactive: "INACTIVE", Exited: "EXITED" };

// [NEW] HR attributes added to the employee master by backend migration 010.
// Each entry: [frontend key, backend column]. Used by toEmployee() and employeeApi.employeeBody().
export const HR_ATTRIBUTE_FIELDS = [
  ["dateOfBirth", "date_of_birth"],
  ["gender", "gender"],
  ["mobilePhone", "mobile_phone"],
  ["personalEmail", "personal_email"],
  ["currentAddress", "current_address"],
  ["legalEntity", "legal_entity"],
  ["businessUnit", "business_unit"],
  // [NEW] default cost center (employee.cost_center). Sent on every PUT, otherwise an edit would erase it.
  ["costCenter", "cost_center"],
  ["primarySkill", "primary_skill"],
  ["professionalExperience", "professional_experience"],
];

// Copies the HR attributes from a backend employee row into the API body shape (null when empty).
// Needed because PUT /employees/{id} replaces every column: a missing field would be erased.
export function hrAttributesFromRow(row = {}) {
  return Object.fromEntries(HR_ATTRIBUTE_FIELDS.map(([, api]) => [api, row?.[api] ?? null]));
}

export function toEmployee(e = {}, lookups, extra = {}) {
  const id = e.employee_id;
  const departmentId = e.department_id ? String(e.department_id) : "";
  const designationId = e.designation_id ? String(e.designation_id) : "";
  const history = e.reporting_history || [];
  const current = [...history].reverse().find((h) => !h.effective_to) || null;
  const managerId = extra.managerId ?? current?.manager_employee_id ?? null;
  rememberName(id, e.employee_name);
  return {
    id,
    name: e.employee_name || nameOf(id),
    email: e.official_email || "",
    phone: e.mobile_phone || "",
    departmentId,
    designationId,
    departmentName: e.department_name || lookups?.departments.find((d) => d.id === departmentId)?.name || "—",
    designationName: e.designation_name || lookups?.designations.find((d) => d.id === designationId)?.name || "—",
    managerId,
    managerName: managerId ? nameOf(managerId) : "—",
    dateOfJoining: e.date_of_joining || "",
    exitDate: e.exit_date || "",
    location: e.location || "",
    // [NEW] migration 010 HR attributes (empty string when not set)
    ...Object.fromEntries(HR_ATTRIBUTE_FIELDS.map(([key, api]) => [key, e[api] || ""])),
    status: EMPLOYMENT[e.employment_status] || "Active",
    role: extra.role || "employee",
    roles: extra.roles || [],
    directReportCount: extra.directReportCount ?? 0,
    reportingHistory: history,
    // [NEW] PM-1 backend-ready: true/false once the backend returns has_login on employees; null when it does not.
    hasLogin: e.has_login === null || e.has_login === undefined ? null : Boolean(Number(e.has_login)), // MariaDB sends 1/0
    raw: e,
  };
}

export function employeeFromRecord(record, lookups) {
  const snap = record.context_snapshot || {};
  return toEmployee({ ...snap, employee_id: record.employee_id }, lookups, { managerId: record.review_manager_employee_id });
}

const HISTORY_LABELS = {
  GOALS_SUBMITTED: "Submitted",
  GOALS_APPROVED: "Approved",
  GOALS_CHANGES_REQUESTED: "Changes requested",
  ASSESSMENT_SUBMITTED: "Assessment submitted",
  RESULT_RELEASED: "Result released",
  RESULT_ACKNOWLEDGED: "Acknowledged",
  REVIEWER_CHANGED: "Reviewer changed",
  DEADLINE_OVERRIDDEN: "Deadline changed",
};

// [NEW] Approval history: load the names of people who acted (and of the reviewing manager) before the history is shown.
// Employees cannot list other people, so each unknown ID is looked up on its own; a failed lookup is ignored.
export async function resolveActorNames(history = [], extraIds = []) {
  const ids = [...new Set([...history.map((h) => h.actor_employee_id), ...extraIds].filter(Boolean).map(String))]
    .filter((id) => !knownNameOf(id));
  await Promise.all(ids.map((id) => api.get(`/employees/${id}`)
    .then((r) => rememberName(id, r.data?.employee_name))
    .catch(() => {})));
}

// [UPDATED] Approval history: show the person who acted. Order: name sent by the backend (actor_name), then the loaded name.
// When the backend saved no actor on a goal approval / change request, the reviewing manager is shown (they are the one who acts).
// "System" stays only for other entries without an actor.
const MANAGER_ACTIONS = ["GOALS_APPROVED", "GOALS_CHANGES_REQUESTED"];
export function toTimeline(history = [], reviewerId = null) {
  return [...history].reverse().map((h, i) => {
    let id = h.actor_employee_id;
    const sent = h.actor_name || h.actor_employee_name;
    if (id && sent) rememberName(id, sent);
    if (!id && reviewerId && MANAGER_ACTIONS.includes(h.event_type)) id = reviewerId;
    let actorName;
    if (!id) actorName = "System";
    else if (knownNameOf(id)) actorName = knownNameOf(id);
    else if (reviewerId && String(id) === String(reviewerId)) actorName = "Reporting manager";
    else actorName = "Team member";
    return {
      id: `${h.event_type}-${h.created_at}-${i}`,
      action: HISTORY_LABELS[h.event_type] || h.event_type,
      actorName,
      comment: stripExtensionTag(h.reason),
      at: isoUtc(h.created_at),
    };
  });
}

export const EVIDENCE_TYPES = {
  "application/pdf": ".pdf", "image/png": ".png", "image/jpeg": ".jpg", "text/plain": ".txt",
};

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new Error("Couldn't read the file."));
    reader.readAsDataURL(file);
  });
}

export function evidenceMime(file) {
  const type = file?.type || "";
  if (EVIDENCE_TYPES[type]) return type;
  const name = (file?.name || "").toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".txt")) return "text/plain";
  return null;
}

export async function downloadFrom(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const disposition = res.headers?.["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = filename || (match ? decodeURIComponent(match[1]) : "download");
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return name;
}

export { notImplemented, getRating };